import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import StudentsManager from '@/components/settings/StudentsManager';
import CategoriesManager from '@/components/settings/CategoriesManager';
import LocationsManager from '@/components/settings/LocationsManager';
import InvoicesManager from '@/components/settings/InvoicesManager';
import TaxesManager from '@/components/settings/TaxesManager';
import TeachersManager from '@/components/settings/TeachersManager';
import ShopManager from '@/components/settings/ShopManager';
import { Users, Tag, MapPin, FileText, Percent, GraduationCap, ShoppingBag } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';
import InvitationModal from '@/components/settings/InvitationModal';
import SpotlightWizard from '@/components/SpotlightWizard';
import CongratsWizardModal from '@/components/CongratsWizardModal';

type WizardStage = 'idle' | 'highlight-categories-tab' | 'highlight-add-category' | 'highlight-teachers-tab' | 'highlight-add-teacher' | 'highlight-students-tab' | 'highlight-add-student';

const SettingsPage = () => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<string>(() => {
    const wizardActive = localStorage.getItem('wizard-active') === 'true';
    const wizardStep = localStorage.getItem('wizard-step');
    if (wizardActive && (wizardStep === '1' || wizardStep === '0')) return 'categories';
    if (wizardActive && wizardStep === '2') return 'students';
    return 'students';
  });

  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false);
  const [categoriesCount, setCategoriesCount] = useState<number | null>(null);
  const [wizardStage, setWizardStage] = useState<WizardStage>('idle');
  const [showCongrats, setShowCongrats] = useState(false);
  const prevCategoriesCountRef = useRef<number | null>(null);
  const prevTeachersCountRef = useRef<number | null>(null);
  const prevStudentsCountRef = useRef<number | null>(null);

  const isSchool = user?.account_type === "S";
  const isSchoolAdmin = isSchool && user?.roleType === 'school_admin';
  const isCoach = user?.account_type === "C";
  const hasShopAccess = user?.hasPaymentMethods;

  // Wizard step semantics:
  //   '1' categories | '2' teachers (school admin only) | '3' students | '4' lesson
  // Initialize wizard stage based on localStorage
  useEffect(() => {
    const evaluate = () => {
      const wizardActive = localStorage.getItem('wizard-active') === 'true';
      const wizardStep = localStorage.getItem('wizard-step');

      if (!wizardActive) {
        setWizardStage('idle');
        return;
      }

      if (wizardStep === '1') {
        setActiveTab('categories');
        setWizardStage('highlight-categories-tab');
      } else if (wizardStep === '2' && isSchoolAdmin) {
        setActiveTab('teachers');
        setWizardStage('highlight-teachers-tab');
      } else if (wizardStep === '2' || wizardStep === '3') {
        setActiveTab('students');
        setWizardStage('highlight-students-tab');
      }
    };
    evaluate();
    window.addEventListener('wizard-state-changed', evaluate);
    return () => window.removeEventListener('wizard-state-changed', evaluate);
  }, [isSchoolAdmin]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('tab') === 'paymentInfo') {
      setActiveTab('invoices');
    }
  }, [location.search]);

  // Handle categories count updates — detect transition 0 -> >0 to advance wizard
  const handleCategoriesLoad = (count: number) => {
    const prev = prevCategoriesCountRef.current;
    prevCategoriesCountRef.current = count;
    setCategoriesCount(count);

    const wizardActive = localStorage.getItem('wizard-active') === 'true';
    const wizardStep = localStorage.getItem('wizard-step');

    if (!wizardActive) return;

    // First load: decide which sub-stage to show on categories tab
    if (prev === null && wizardStep === '1') {
      if (count === 0) {
        return;
      } else {
        // Already has categories — skip to next step (teachers for school, students for coach)
        const nextStep = isSchoolAdmin ? '2' : '3';
        localStorage.setItem('wizard-step', nextStep);
        window.dispatchEvent(new Event('wizard-state-changed'));
        if (isSchoolAdmin) {
          setActiveTab('teachers');
          setWizardStage('highlight-teachers-tab');
        } else {
          setActiveTab('students');
          setWizardStage('highlight-students-tab');
        }
        return;
      }
    }

    // Detect a brand-new category was added (transition 0 -> >0) → advance
    if (prev !== null && prev === 0 && count > 0 && wizardStep === '1') {
      const nextStep = isSchoolAdmin ? '2' : '3';
      localStorage.setItem('wizard-step', nextStep);
      window.dispatchEvent(new Event('wizard-state-changed'));
      setTimeout(() => {
        if (isSchoolAdmin) {
          setActiveTab('teachers');
          setWizardStage('highlight-teachers-tab');
        } else {
          setActiveTab('students');
          setWizardStage('highlight-students-tab');
        }
      }, 400);
    }
  };

  // Teachers count updates (school admin) → advance to students step
  const handleTeachersLoad = (count: number) => {
    const prev = prevTeachersCountRef.current;
    prevTeachersCountRef.current = count;

    const wizardActive = localStorage.getItem('wizard-active') === 'true';
    const wizardStep = localStorage.getItem('wizard-step');
    if (!wizardActive || !isSchoolAdmin) return;

    if (prev === null && wizardStep === '2') {
      if (count > 0) {
        // Already has teachers → skip to students
        localStorage.setItem('wizard-step', '3');
        window.dispatchEvent(new Event('wizard-state-changed'));
        setActiveTab('students');
        setWizardStage('highlight-students-tab');
      }
      return;
    }

    if (prev !== null && prev === 0 && count > 0 && wizardStep === '2') {
      localStorage.setItem('wizard-step', '3');
      window.dispatchEvent(new Event('wizard-state-changed'));
      setTimeout(() => {
        setActiveTab('students');
        setWizardStage('highlight-students-tab');
      }, 400);
    }
  };

  // Tab acks
  const handleCategoriesTabAck = () => setWizardStage('highlight-add-category');
  const handleTeachersTabAck = () => setWizardStage('highlight-add-teacher');
  const handleStudentsTabAck = () => setWizardStage('highlight-add-student');

  const closeWizard = () => {
    setWizardStage('idle');
    localStorage.setItem('wizard-active', 'false');
    window.dispatchEvent(new Event('wizard-state-changed'));
  };

  const handleAddCategoryClicked = () => setWizardStage('idle');
  const handleAddTeacherClicked = () => setWizardStage('idle');
  const handleAddStudentClicked = () => setWizardStage('idle');

  // If user cancels/dismisses the Add dialog without creating, restore spotlight
  useEffect(() => {
    const handleDialogClosed = (e: Event) => {
      const wizardActive = localStorage.getItem('wizard-active') === 'true';
      if (!wizardActive) return;
      const detail = (e as CustomEvent).detail;
      setTimeout(() => {
        const stillActive = localStorage.getItem('wizard-active') === 'true';
        const currentStep = localStorage.getItem('wizard-step');
        if (!stillActive) return;
        if (detail?.type === 'category' && currentStep === '1') {
          setActiveTab('categories');
          setWizardStage('highlight-add-category');
        } else if (detail?.type === 'teacher' && currentStep === '2' && isSchoolAdmin) {
          setActiveTab('teachers');
          setWizardStage('highlight-add-teacher');
        } else if (detail?.type === 'student' && (currentStep === '2' || currentStep === '3')) {
          setActiveTab('students');
          setWizardStage('highlight-add-student');
        }
      }, 300);
    };
    window.addEventListener('wizard-add-dialog-closed', handleDialogClosed);
    return () => window.removeEventListener('wizard-add-dialog-closed', handleDialogClosed);
  }, [isSchoolAdmin]);

  // Called by StudentsManager when student list count changes
  const handleStudentsLoad = (count: number) => {
    const prev = prevStudentsCountRef.current;
    prevStudentsCountRef.current = count;

    const wizardActive = localStorage.getItem('wizard-active') === 'true';
    const wizardStep = localStorage.getItem('wizard-step');
    if (!wizardActive) return;

    // Student created during students step → show congrats
    if (count > 0 && (wizardStep === '2' || wizardStep === '3')) {
      // For coaches: step '2' is students. For schools: step '3' is students.
      // Skip if school is still on teachers step (count of students may be loaded but step is '2' = teachers).
      if (isSchoolAdmin && wizardStep === '2') return;
      localStorage.setItem('wizard-step', '4');
      window.dispatchEvent(new Event('wizard-state-changed'));
      setWizardStage('idle');
      setShowCongrats(true);
    }
  };

  const handleCongratsClose = () => {
    setShowCongrats(false);
    localStorage.setItem('wizard-active', 'false');
    window.dispatchEvent(new Event('wizard-state-changed'));
  };

  const handleCongratsCreateLesson = () => {
    setShowCongrats(false);
    localStorage.setItem('wizard-active', 'true');
    localStorage.setItem('wizard-step', '4');
    window.dispatchEvent(new Event('wizard-state-changed'));
    navigate('/agenda?wizard=lesson');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1 container py-8 mt-16">
        <Card className={!isMobile ? 'border shadow-md' : 'border-0 shadow-none'}>
          <CardHeader className={!isMobile ? '' : 'p-2'}>
            <CardTitle>{t('settings.cardTitle')}</CardTitle>
          </CardHeader>
          <CardContent className={isMobile ? 'p-1' : 'p-4'}>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              {isMobile ? (
                <Select value={activeTab} onValueChange={setActiveTab}>
                  <SelectTrigger
                    id={
                      activeTab === 'categories' ? 'settings-categories-tab'
                      : activeTab === 'teachers' ? 'settings-teachers-tab'
                      : activeTab === 'students' ? 'settings-students-tab'
                      : undefined
                    }
                    className="w-full"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="students">
                      <span className="flex items-center gap-2"><Users className="h-4 w-4" />{t('settings.tabs.students')}</span>
                    </SelectItem>
                    {isSchoolAdmin && (
                      <SelectItem value="teachers">
                        <span className="flex items-center gap-2"><GraduationCap className="h-4 w-4" />{t('settings.tabs.teachers')}</span>
                      </SelectItem>
                    )}
                    <SelectItem value="categories">
                      <span className="flex items-center gap-2"><Tag className="h-4 w-4" />{t('settings.tabs.categories')}</span>
                    </SelectItem>
                    <SelectItem value="locations">
                      <span className="flex items-center gap-2"><MapPin className="h-4 w-4" />{t('settings.tabs.locations')}</span>
                    </SelectItem>
                    <SelectItem value="taxes">
                      <span className="flex items-center gap-2"><Percent className="h-4 w-4" />{t('settings.tabs.taxes')}</span>
                    </SelectItem>
                    <SelectItem value="invoices">
                      <span className="flex items-center gap-2"><FileText className="h-4 w-4" />{t('settings.tabs.invoices')}</span>
                    </SelectItem>
                    {hasShopAccess && (
                      <SelectItem value="shop">
                        <span className="flex items-center gap-2"><ShoppingBag className="h-4 w-4" />{t('settings.tabs.shop') || 'Shop'}</span>
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <TabsList className={`grid w-full ${isSchoolAdmin ? (hasShopAccess ? 'grid-cols-7' : 'grid-cols-6') : (hasShopAccess ? 'grid-cols-6' : 'grid-cols-5')}`}>
                  <TabsTrigger value="students" id="settings-students-tab" className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <div>{t('settings.tabs.students')}</div>
                  </TabsTrigger>
                  {isSchoolAdmin && (
                    <TabsTrigger value="teachers" id="settings-teachers-tab" className="flex items-center gap-2">
                      <GraduationCap className="h-4 w-4" />
                      <div>{t('settings.tabs.teachers')}</div>
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="categories" id="settings-categories-tab" className="flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    <div>{t('settings.tabs.categories')}</div>
                  </TabsTrigger>
                  <TabsTrigger value="locations" className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <div>{t('settings.tabs.locations')}</div>
                  </TabsTrigger>
                  <TabsTrigger value="taxes" className="flex items-center gap-2">
                    <Percent className="h-4 w-4" />
                    <div>{t('settings.tabs.taxes')}</div>
                  </TabsTrigger>
                  <TabsTrigger value="invoices" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <div>{t('settings.tabs.invoices')}</div>
                  </TabsTrigger>
                  {hasShopAccess && (
                    <TabsTrigger value="shop" className="flex items-center gap-2">
                      <ShoppingBag className="h-4 w-4" />
                      <div>{t('settings.tabs.shop') || 'Shop'}</div>
                    </TabsTrigger>
                  )}
                </TabsList>
              )}

              <div>
                <TabsContent value="students" className="mt-6">
                  <StudentsManager onStudentsLoad={handleStudentsLoad} />
                </TabsContent>

                <TabsContent value="categories" className="mt-6">
                  <CategoriesManager onCategoriesLoad={handleCategoriesLoad} />
                </TabsContent>

                <TabsContent value="locations" className="mt-6">
                  <LocationsManager />
                </TabsContent>

                <TabsContent value="invoices" className="mt-6">
                  <InvoicesManager />
                </TabsContent>

                <TabsContent value="taxes" className="mt-6">
                  <TaxesManager />
                </TabsContent>

                {hasShopAccess && (
                  <TabsContent value="shop" className="mt-6">
                    <ShopManager />
                  </TabsContent>
                )}

                {isSchoolAdmin && (
                  <TabsContent value="teachers" className="mt-6">
                    <TeachersManager
                      showInvitationButton={isSchool}
                      onInviteClick={() => setIsInvitationModalOpen(true)}
                      onTeachersLoad={handleTeachersLoad}
                    />
                  </TabsContent>
                )}
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </main>

      <Footer />

      <InvitationModal
        isOpen={isInvitationModalOpen}
        onClose={() => setIsInvitationModalOpen(false)}
        userType="school"
      />

      {/* Stage 1: highlight Categories tab — user clicks OK */}
      <SpotlightWizard
        isVisible={wizardStage === 'highlight-categories-tab' && !user?.hasCreatedCategory}
        targetSelector="#settings-categories-tab"
        message={t('wizard.categoriesTab') || "Voici l'onglet Catégories. Cliquez sur OK pour continuer."}
        position="bottom"
        showCloseButton={true}
        onClose={handleCategoriesTabAck}
      />

      {/* Stage 2: highlight Add Category button — no OK, user must click the button */}
      <SpotlightWizard
        isVisible={wizardStage === 'highlight-add-category' && !user?.hasCreatedCategory}
        targetSelector="#settings-categories-add-btn"
        message={t('wizard.addCategory') || "Cliquez sur Ajouter pour créer votre première catégorie."}
        position="bottom"
        showCloseButton={false}
        closeOnTargetClick={true}
        onClose={handleAddCategoryClicked}
      />

      {/* Stage 3a (school only): highlight Teachers tab — user clicks OK */}
      <SpotlightWizard
        isVisible={wizardStage === 'highlight-teachers-tab' && !user?.hasCreatedTeacher && isSchoolAdmin}
        targetSelector="#settings-teachers-tab"
        message={t('wizard.teachersTab') || "Maintenant ajoutez votre premier professeur. Cliquez sur OK pour continuer."}
        position="bottom"
        showCloseButton={true}
        onClose={handleTeachersTabAck}
      />

      {/* Stage 3b (school only): highlight Add Teacher button */}
      <SpotlightWizard
        isVisible={wizardStage === 'highlight-add-teacher' && !user?.hasCreatedTeacher && isSchoolAdmin}
        targetSelector="#settings-teachers-add-btn"
        message={t('wizard.addTeacher') || "Cliquez sur Ajouter pour créer votre premier professeur."}
        position="bottom"
        showCloseButton={false}
        closeOnTargetClick={true}
        onClose={handleAddTeacherClicked}
      />

      {/* Stage 4: highlight Students tab — user clicks OK */}
      <SpotlightWizard
        isVisible={wizardStage === 'highlight-students-tab' && !user?.hasCreatedStudent}
        targetSelector="#settings-students-tab"
        message={t('wizard.studentsTab') || "Parfait ! Maintenant cliquez sur OK pour ajouter vos élèves."}
        position="bottom"
        showCloseButton={true}
        onClose={handleStudentsTabAck}
      />

      {/* Stage 5: highlight Add Student button — no OK */}
      <SpotlightWizard
        isVisible={wizardStage === 'highlight-add-student' && !user?.hasCreatedStudent}
        targetSelector="#settings-students-add-btn"
        message={t('wizard.addStudent') || "Cliquez sur Ajouter pour créer votre premier élève."}
        position="bottom"
        showCloseButton={false}
        closeOnTargetClick={true}
        onClose={handleAddStudentClicked}
      />

      <CongratsWizardModal
        isOpen={showCongrats}
        onClose={handleCongratsClose}
        onCreateLesson={handleCongratsCreateLesson}
      />
    </div>
  );
};

export default SettingsPage;
